//******************************************************************************************
// QuestionAnswerManagementPopup Component: Provides an interface for managing a list of
// question-answer pairs. Supports adding, deleting, and reordering entries with swap
// functionality for prioritizing questions.
//******************************************************************************************

import React, { useState, useEffect } from "react";
import "./QuestionAnswerManagementPopup.scss";
import axios from "axios";
import { FiTrash2, FiArrowUp, FiArrowDown, FiAlertTriangle } from "react-icons/fi";
import { FaRegCommentDots, FaQuestionCircle } from "react-icons/fa";

const QuestionAnswerManagementPopup = ({ onClose }) => {
  const [activeTab, setActiveTab] = useState("welcome");
  const [entries, setEntries] = useState([]);
  const [newEntry, setNewEntry] = useState({ question: "", answer: "" });
  const [faqMessage, setFaqMessage] = useState("");
  const [welcomeMessage, setWelcomeMessage] = useState("");
  const [startingMessages, setStartingMessages] = useState({
    active: "",
    inactive: "",
    secondary: "",
    secondaryEnabled: false,
  });
  const [hasImproperLinks, setHasImproperLinks] = useState({
    welcome: false,
    faq: false,
  });

  // Fetch initial entries when the component mounts
  useEffect(() => {
    fetchEntries();
    fetchStartingMessages();
  }, []);

  // Clear messages when changing tabs
  useEffect(() => {
    setFaqMessage("");
    setWelcomeMessage("");
  }, [activeTab]);

  // Check for improperly formatted links whenever relevant content changes
  useEffect(() => {
    checkImproperLinksInAllTabs();
  }, [startingMessages, newEntry, entries]);

  const fetchEntries = async () => {
    try {
      const baseUrl = process.env.REACT_APP_API_URL;
      const apiUrl = new URL("/api/config/questions", baseUrl).toString();

      const token = localStorage.getItem("admin-token");
      const response = await axios.get(apiUrl, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.success) {
        setEntries(response.data.config.entries || []);
      } else {
        setFaqMessage("Falha ao carregar entradas.");
      }
    } catch (error) {
      console.error("Error fetching Q&A entries:", error);
      setFaqMessage("Erro ao carregar entradas.");
    }
  };

  const fetchStartingMessages = async () => {
    try {
      const apiUrl = `${process.env.REACT_APP_API_URL}/api/config/starting-messages`;
      const token = localStorage.getItem("admin-token");
      const response = await axios.get(apiUrl, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.success) {
        setStartingMessages(response.data.startingMessages);
      }
    } catch (error) {
      console.error("Error fetching starting messages:", error);
      setWelcomeMessage("Erro ao carregar mensagens de boas-vindas.");
    }
  };

  // Function to check if text contains any links that aren't properly formatted
  const hasImproperlyFormattedLinks = (text) => {
    if (!text) return false;

    // Simple URL pattern for detecting URLs
    const urlPattern = /(https?:\/\/[^\s\[\]()]+|www\.[^\s\[\]()]+\.[^\s\[\]()]+|[^\s\[\]()]+\.[^\s\[\]()]+\.[^\s\[\]()]+)/i;

    // Check if there's a markdown-like structure with spaces between ] and (
    const spaceBetweenBracketsAndParens = /\][^\(]+\(/g.test(text);
    if (spaceBetweenBracketsAndParens) {
      return true;
    }

    // Look for malformed markdown structures like [url)(text]
    if (text.includes(")(")) {
      return true;
    }

    // Find and process all markdown-like structures in the text
    // This includes both [text](link) and [link][link] formats
    const standardMarkdownLinks = text.match(/\[[^\]]+\]\([^)]+\)/g) || [];
    const squareBracketLinks = text.match(/\[[^\]]+\]\[[^\]]+\]/g) || [];

    // Check standard markdown links [text](link)
    for (const structure of standardMarkdownLinks) {
      const bracketContent = structure.match(/\[([^\]]+)\]/)[1];
      const parenthesesContent = structure.match(/\(([^)]+)\)/)[1];

      // If the URL is in brackets and descriptive text is in parentheses, it's improper
      if (urlPattern.test(bracketContent) && !urlPattern.test(parenthesesContent)) {
        return true;
      }

      // If both bracket and parentheses contain different URLs, it's improper
      if (urlPattern.test(bracketContent) && urlPattern.test(parenthesesContent) && bracketContent !== parenthesesContent) {
        return true;
      }
    }

    // Check for double-bracket links [link][samelink]
    for (const structure of squareBracketLinks) {
      const matches = structure.match(/\[([^\]]+)\]\[([^\]]+)\]/);
      if (matches) {
        const firstContent = matches[1];
        const secondContent = matches[2];

        // If both bracket contents are identical, consider it a valid format
        if (firstContent === secondContent) {
          // This is a valid format, remove it from further consideration
          text = text.replace(structure, "");
        }
      }
    }

    // Remove all valid markdown links from the text
    text = text.replace(/\[[^\]]+\]\([^)]+\)/g, "");

    // Check if there are any URLs left in the plain text
    const unmatchedUrls = text.match(urlPattern);

    return unmatchedUrls !== null;
  };

  // Check both tabs for improper links
  const checkImproperLinksInAllTabs = () => {
    // Check welcome tab (starting messages)
    const welcomeTabTexts = [startingMessages.active, startingMessages.inactive, startingMessages.secondary];
    const welcomeHasImproper = welcomeTabTexts.some((text) => text && text.trim().length > 0 && hasImproperlyFormattedLinks(text));

    // Check FAQ tab (entries and new entry)
    const faqTabTexts = [newEntry.question, newEntry.answer, ...entries.map((entry) => entry.question), ...entries.map((entry) => entry.answer)];
    const faqHasImproper = faqTabTexts.some((text) => text && text.trim().length > 0 && hasImproperlyFormattedLinks(text));

    // Update state with results for both tabs
    setHasImproperLinks({
      welcome: welcomeHasImproper,
      faq: faqHasImproper,
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (value.length <= 1500) {
      setNewEntry((prev) => {
        const updated = { ...prev, [name]: value };

        // Check for improper links in FAQ tab only
        setTimeout(() => {
          const faqTabTexts = [
            name === "question" ? value : prev.question,
            name === "answer" ? value : prev.answer,
            ...entries.map((entry) => entry.question),
            ...entries.map((entry) => entry.answer),
          ];

          const faqHasImproper = faqTabTexts.some((text) => text && text.trim().length > 0 && hasImproperlyFormattedLinks(text));

          // Update only the FAQ tab status
          setHasImproperLinks((prev) => ({
            ...prev,
            faq: faqHasImproper,
          }));
        }, 0);

        return updated;
      });
    }
  };

  const handleAddEntry = async () => {
    if (!newEntry.question || !newEntry.answer) {
      setFaqMessage("Preencha questão e resposta.");
      return;
    }
    if (entries.length >= 10) {
      setFaqMessage("Máximo de 10 entradas atingido.");
      return;
    }

    try {
      const baseUrl = process.env.REACT_APP_API_URL;
      const apiUrl = new URL("/api/config/questions/add", baseUrl).toString();

      const token = localStorage.getItem("admin-token");
      const response = await axios.put(apiUrl, { question: newEntry.question, answer: newEntry.answer }, { headers: { Authorization: `Bearer ${token}` } });

      if (response.data.success) {
        const updatedEntries = response.data.config.entries;
        setEntries(updatedEntries);
        setNewEntry({ question: "", answer: "" });
        setFaqMessage("Entrada adicionada com sucesso.");

        // Check FAQ tab for improper links after adding entry
        setTimeout(() => {
          const faqTabTexts = [...updatedEntries.map((entry) => entry.question), ...updatedEntries.map((entry) => entry.answer)];

          const faqHasImproper = faqTabTexts.some((text) => text && text.trim().length > 0 && hasImproperlyFormattedLinks(text));

          setHasImproperLinks((prev) => ({
            ...prev,
            faq: faqHasImproper,
          }));
        }, 0);
      } else {
        setFaqMessage("Falha ao adicionar entrada.");
      }
    } catch (error) {
      console.error("Error adding entry:", error);
      setFaqMessage("Erro ao adicionar entrada.");
    }
  };

  const handleDeleteEntry = async (index) => {
    try {
      const baseUrl = process.env.REACT_APP_API_URL;
      const apiUrl = new URL(`/api/config/questions/${index}`, baseUrl).toString();

      const token = localStorage.getItem("admin-token");
      const response = await axios.delete(apiUrl, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.success) {
        const updatedEntries = response.data.config.entries;
        setEntries(updatedEntries);
        setFaqMessage("Entrada apagada com sucesso.");

        // Check FAQ tab for improper links after deleting entry
        setTimeout(() => {
          const faqTabTexts = [
            newEntry.question,
            newEntry.answer,
            ...updatedEntries.map((entry) => entry.question),
            ...updatedEntries.map((entry) => entry.answer),
          ];

          const faqHasImproper = faqTabTexts.some((text) => text && text.trim().length > 0 && hasImproperlyFormattedLinks(text));

          setHasImproperLinks((prev) => ({
            ...prev,
            faq: faqHasImproper,
          }));
        }, 0);
      } else {
        setFaqMessage("Falha ao apagar entrada.");
      }
    } catch (error) {
      console.error("Error deleting entry:", error);
      setFaqMessage("Erro ao apagar entrada.");
    }
  };

  const handleSaveMessages = async () => {
    try {
      const apiUrl = `${process.env.REACT_APP_API_URL}/api/config/starting-messages`;
      const token = localStorage.getItem("admin-token");
      const response = await axios.put(apiUrl, startingMessages, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.success) {
        setWelcomeMessage("Mensagens de boas-vindas atualizadas com sucesso.");
      } else {
        setWelcomeMessage("Falha ao atualizar as mensagens de boas-vindas.");
      }
    } catch (error) {
      console.error("Error updating starting messages:", error);
      setWelcomeMessage("Erro ao Guardar mensagens.");
    }
  };

  const handleSwapEntries = async (index1, index2) => {
    try {
      const baseUrl = process.env.REACT_APP_API_URL;
      const apiUrl = new URL("/api/config/questions/swap", baseUrl).toString();

      const token = localStorage.getItem("admin-token");
      const response = await axios.put(apiUrl, { index1, index2 }, { headers: { Authorization: `Bearer ${token}` } });

      if (response.data.success) {
        const updatedEntries = response.data.config.entries;
        setEntries(updatedEntries);
        setFaqMessage("Ordem alterada com sucesso.");

        // Check FAQ tab for improper links after reordering
        setTimeout(() => {
          const faqTabTexts = [
            newEntry.question,
            newEntry.answer,
            ...updatedEntries.map((entry) => entry.question),
            ...updatedEntries.map((entry) => entry.answer),
          ];

          const faqHasImproper = faqTabTexts.some((text) => text && text.trim().length > 0 && hasImproperlyFormattedLinks(text));

          setHasImproperLinks((prev) => ({
            ...prev,
            faq: faqHasImproper,
          }));
        }, 0);
      } else {
        setFaqMessage("Falha ao alterar a ordem.");
      }
    } catch (error) {
      console.error("Error swapping entries:", error);
      setFaqMessage("Falha ao alterar a ordem.");
    }
  };

  const handleMoveUp = (index) => {
    if (index === 0) return;
    handleSwapEntries(index, index - 1);
  };

  const handleMoveDown = (index) => {
    if (index === entries.length - 1) return;
    handleSwapEntries(index, index + 1);
  };

  // ----- TABS RENDERING -----
  const renderTabs = () => (
    <div className="qa-management-popup__tabs">
      <div className={`qa-management-popup__tab ${activeTab === "welcome" ? "active" : ""}`} onClick={() => setActiveTab("welcome")}>
        <FaRegCommentDots />
        <span>Mensagens</span>
      </div>
      <div className={`qa-management-popup__tab ${activeTab === "faq" ? "active" : ""}`} onClick={() => setActiveTab("faq")}>
        <FaQuestionCircle />
        <span>FAQs</span>
      </div>
    </div>
  );

  const renderWelcomeTab = () => (
    <div className="qa-management-popup__tab-body">
      <h3>Mensagens de Boas-Vindas</h3>
      <div className={`qa-management-popup__message ${welcomeMessage ? "visible" : ""}`} aria-live="polite" style={{ minHeight: "30px" }}>
        {welcomeMessage && <p>{welcomeMessage}</p>}
      </div>

      <div className="qa-management-popup__form-group">
        <label>Mensagem Inicial Bot Ativo:</label>
        <textarea
          value={startingMessages.active}
          onChange={(e) => {
            setStartingMessages((prev) => {
              const updated = { ...prev, active: e.target.value };

              // Delay checking to ensure state is updated first
              setTimeout(() => {
                // Check welcome tab texts
                const welcomeTabTexts = [e.target.value, prev.inactive, prev.secondary];

                const welcomeHasImproper = welcomeTabTexts.some((text) => text && text.trim().length > 0 && hasImproperlyFormattedLinks(text));

                // Update only the welcome tab status
                setHasImproperLinks((prev) => ({
                  ...prev,
                  welcome: welcomeHasImproper,
                }));
              }, 0);

              return updated;
            });
          }}
          maxLength="1500"
        />
      </div>
      <div className="qa-management-popup__form-group">
        <label>Mensagem Inicial Bot Inativo:</label>
        <textarea
          value={startingMessages.inactive}
          onChange={(e) => {
            setStartingMessages((prev) => {
              const updated = { ...prev, inactive: e.target.value };

              // Delay checking to ensure state is updated first
              setTimeout(() => {
                // Check welcome tab texts
                const welcomeTabTexts = [prev.active, e.target.value, prev.secondary];

                const welcomeHasImproper = welcomeTabTexts.some((text) => text && text.trim().length > 0 && hasImproperlyFormattedLinks(text));

                // Update only the welcome tab status
                setHasImproperLinks((prev) => ({
                  ...prev,
                  welcome: welcomeHasImproper,
                }));
              }, 0);

              return updated;
            });
          }}
          maxLength="1500"
        />
      </div>
      <div className="qa-management-popup__form-group">
        <label>Mensagem Secundária (Opcional):</label>
        <textarea
          value={startingMessages.secondary}
          onChange={(e) => {
            setStartingMessages((prev) => {
              const updated = { ...prev, secondary: e.target.value };

              // Delay checking to ensure state is updated first
              setTimeout(() => {
                // Check welcome tab texts
                const welcomeTabTexts = [prev.active, prev.inactive, e.target.value];

                const welcomeHasImproper = welcomeTabTexts.some((text) => text && text.trim().length > 0 && hasImproperlyFormattedLinks(text));

                // Update only the welcome tab status
                setHasImproperLinks((prev) => ({
                  ...prev,
                  welcome: welcomeHasImproper,
                }));
              }, 0);

              return updated;
            });
          }}
          maxLength="1500"
        />
        <label className="qa-management-popup__checkbox-label">
          <input
            type="checkbox"
            checked={startingMessages.secondaryEnabled}
            onChange={(e) => {
              setStartingMessages((prev) => {
                const updated = {
                  ...prev,
                  secondaryEnabled: e.target.checked,
                };

                // Recheck welcome tab for improper links
                setTimeout(() => {
                  const welcomeTabTexts = [prev.active, prev.inactive, prev.secondary];

                  const welcomeHasImproper = welcomeTabTexts.some((text) => text && text.trim().length > 0 && hasImproperlyFormattedLinks(text));

                  // Update only the welcome tab status
                  setHasImproperLinks((prev) => ({
                    ...prev,
                    welcome: welcomeHasImproper,
                  }));
                }, 0);

                return updated;
              });
            }}
          />
          Ativar Mensagem Secundária
        </label>
      </div>
      <button className="qa-management-popup__save-button" onClick={handleSaveMessages}>
        Guardar Mensagens
      </button>
    </div>
  );

  const renderFAQTab = () => (
    <div className="qa-management-popup__tab-body">
      <h3>Gestão de FAQs</h3>
      <div className={`qa-management-popup__message ${faqMessage ? "visible" : ""}`} aria-live="polite" style={{ minHeight: "30px" }}>
        {faqMessage && <p>{faqMessage}</p>}
      </div>
      <div className="qa-management-popup__input-container">
        <textarea
          name="question"
          placeholder="Escreva a questão (máx. 140 caracteres)"
          value={newEntry.question}
          onChange={handleInputChange}
          aria-label="Enter question"
          maxLength="140"
        />
        <textarea
          name="answer"
          placeholder="Escreva a resposta (máx. 140 caracteres)"
          value={newEntry.answer}
          onChange={handleInputChange}
          aria-label="Enter answer"
          maxLength="140"
        />
        <button onClick={handleAddEntry} disabled={entries.length >= 10} aria-disabled={entries.length >= 10}>
          Adicionar
        </button>
      </div>

      <ul className="qa-management-popup__list">
        {(entries || []).map((entry, index) => (
          <li key={index} className="qa-management-popup__item">
            <div className="qa-management-popup__priority">
              <button onClick={() => handleMoveUp(index)} disabled={index === 0} aria-label="Move up" className="qa-management-popup__arrow">
                <FiArrowUp />
              </button>
              <span className="qa-management-popup__priority-number">{index + 1}</span>
              <button
                onClick={() => handleMoveDown(index)}
                disabled={index === entries.length - 1}
                aria-label="Move down"
                className="qa-management-popup__arrow"
              >
                <FiArrowDown />
              </button>
            </div>
            <span className="qa-management-popup__item-text">
              <strong>Q:</strong> {entry.question} <br />
              <strong>A:</strong> {entry.answer}
            </span>
            <button
              onClick={() => handleDeleteEntry(index)}
              className="qa-management-popup__delete"
              title="Delete entry"
              aria-label={`Delete question ${index + 1}`}
            >
              <FiTrash2 />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );

  return (
    <div className="qa-management-popup">
      <div className="qa-management-popup__overlay" onClick={onClose} aria-label="Close popup overlay" />
      <div className="qa-management-popup__content" role="dialog" aria-labelledby="qa-popup-title">
        {renderTabs()}
        {activeTab === "welcome" && renderWelcomeTab()}
        {activeTab === "faq" && renderFAQTab()}
        {(activeTab === "welcome" && hasImproperLinks.welcome) || (activeTab === "faq" && hasImproperLinks.faq) ? (
          <div className="infoText">
            <FiAlertTriangle style={{ marginRight: "5px", color: "#f39c12" }} />
            Nota: Formatar links [Texto](link)
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default QuestionAnswerManagementPopup;
